import React, { FC, useEffect, useMemo, useState } from 'react'
import { CircularProgress } from '@material-ui/core'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useUserInfo } from 'src/services'
import { CellularPayPrompt, CellularPayResult, isBankChoice, PaymentMethodChoice } from './types'
import { CreditCardIcon } from './icons'
import { useDialogStyles } from './styles'
import { useFormStyles } from './form-styles'
import {
  NextInvoiceLine,
  PromptButton,
  PromptDialog,
  PromptExtras,
  PromptFooter,
  PromptHeader
} from './dialog-parts'
import { CardFormValue, CellularCardForm, EMPTY_CARD, isCardReady } from './cellular-card-form'
import { hasPaymentChoices, PaymentMethodOptions } from './payment-method-options'
import { controllersLabel, formatMoney, periodsLabel } from './format'
import { useCellularPayment } from './use-cellular-payment'
import { FailureNotice, HostedInvoiceLink } from './payment-failure'
import { useBankPayment } from './use-bank-payment'
import { BankAccountForm, BankMandate } from './bank-account-form'
import { isSimOff } from './sim-status'
import { isAchOffered } from './ach'

interface PaymentDialogProps {
  open: boolean
  accountId: string
  prompt: CellularPayPrompt
  onBack: () => void
  onPaid: () => void
  // Refetches the prompt without closing this dialog (a card was just saved).
  onCardSaved: () => void
  // Refetches the prompt when the customer asks for the bill's new amount.
  onRefresh: () => Promise<unknown>
  // A bank payment started (or waits for the test deposits): it counts as paid for now.
  onBankStarted: (result: CellularPayResult) => void
}

type BodyProps = Omit<PaymentDialogProps, 'open'> & {
  // Whether Stripe's bank window may be open: a new bank account is being paid with.
  onBankWindow: (open: boolean) => void
}

const TITLE_ID = 'cellular-payment-title'
const DESCRIPTION_ID = 'cellular-payment-description'

// Card entry needs Stripe.js, which needs the publishable key to be configured.
const publishableKey = (): string | undefined => process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY

const CARD_ENTRY_UNAVAILABLE = 'We cannot take new card details here at the moment.'

// Loads Inter inside Stripe's card iframes so they match the mock's inputs.
const ELEMENTS_OPTIONS = {
  fonts: [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500&display=swap' }]
}

// The method the dialog opens on: the saved bank account when the subscription renews
// on it, else a saved card, else a saved bank account, else a new card.
const firstChoice = (prompt: CellularPayPrompt): PaymentMethodChoice => {
  const savedBank = isAchOffered(prompt) ? prompt.savedBankAccount : null
  if (savedBank?.renewsOn) return 'savedBank'
  if (prompt.savedCard) return 'saved'
  return savedBank ? 'savedBank' : 'new'
}

const PaymentDialogBody: FC<BodyProps> = ({
  accountId,
  prompt,
  onBack,
  onPaid,
  onCardSaved,
  onRefresh,
  onBankStarted,
  onBankWindow
}) => {
  const classes = useDialogStyles()
  const formClasses = useFormStyles()
  const me = useUserInfo()?.me
  const { savedCard } = prompt
  const bankOffered = isAchOffered(prompt)
  const [method, setMethod] = useState<PaymentMethodChoice>(firstChoice(prompt))
  const [card, setCard] = useState<CardFormValue>(EMPTY_CARD)
  const [holderName, setHolderName] = useState<string>(
    `${me?.firstName || ''} ${me?.lastName || ''}`.trim()
  )
  const cardPayment = useCellularPayment({
    accountId,
    prompt,
    method,
    cardholderName: card.name,
    cardReady: isCardReady(card),
    onPaid,
    onCardSaved
  })
  const bankPayment = useBankPayment({
    accountId,
    prompt,
    method,
    holderName,
    email: me?.email,
    onPaid,
    onStarted: onBankStarted
  })
  const isBank = isBankChoice(method)
  const bankWindow = method === 'bank' && bankPayment.paying
  useEffect(() => onBankWindow(bankWindow), [bankWindow, onBankWindow])
  const paying = cardPayment.paying || bankPayment.paying
  const failure = isBank ? bankPayment.failure : cardPayment.failure
  const clearFailure = () => {
    cardPayment.clearFailure()
    bankPayment.clearFailure()
  }
  const { startNewCardEntry } = cardPayment
  const canPay = isBank ? bankPayment.canPay : cardPayment.canPay
  const pay = () => (isBank ? bankPayment.pay() : cardPayment.pay())
  const canEnterCard = Boolean(publishableKey())
  const total = formatMoney(prompt.amountDueUsd, prompt.currency)
  // A back-bill's rows are not one year each (seat and extra lines share dates), so
  // it names the controllers it pays for instead.
  const billed = prompt.isBackbill ? prompt.billedControllers || [] : []
  // Omitted when the periods are not whole years, or when there are none at all.
  const periods =
    billed.length > 0 ? `for ${controllersLabel(billed.length)}` : periodsLabel(prompt.periods)

  const chooseMethod = (next: PaymentMethodChoice) => {
    clearFailure()
    setMethod(next)
  }
  const refreshAmount = async () => {
    await onRefresh()
    clearFailure()
  }
  const changeCard = (update: (previous: CardFormValue) => CardFormValue) => {
    clearFailure()
    // Editing the fields after a card was saved starts a new card: it needs its own SetupIntent.
    startNewCardEntry()
    setCard(update)
  }

  return (
    <>
      <PromptHeader
        descriptionId={DESCRIPTION_ID}
        icon={<CreditCardIcon size={28} />}
        id={TITLE_ID}
        title='Confirm your payment method'
        tone='primary'
      >
        You are paying <span className={classes.strong}>{total}</span>
        {periods ? ` ${periods}` : ''}. Partial payments are not accepted.
      </PromptHeader>
      {hasPaymentChoices({ savedCard, bankOffered }) ? (
        <PaymentMethodOptions
          bankOffered={bankOffered}
          disabled={paying}
          method={method}
          onChange={chooseMethod}
          savedBankAccount={prompt.savedBankAccount}
          savedCard={savedCard}
        />
      ) : (
        <p className={formClasses.heading}>Enter your card details</p>
      )}
      {method === 'bank' && (
        <BankAccountForm
          amount={total}
          disabled={paying}
          holderName={holderName}
          simOff={isSimOff(prompt)}
          onChange={name => {
            clearFailure()
            setHolderName(name)
          }}
        />
      )}
      {method === 'savedBank' && <BankMandate amount={total} />}
      {method === 'new' &&
        (canEnterCard ? (
          <CellularCardForm
            disabled={paying}
            onChange={changeCard}
            value={card}
          />
        ) : (
          // Without a publishable key the card fields can never load, and Pay would
          // just sit there disabled. Say so, and offer Stripe's page instead.
          <div role='alert'>
            <p className={classes.error}>
              {CARD_ENTRY_UNAVAILABLE}
              {prompt.hostedInvoiceUrl ? '' : ' Please try again shortly.'}
            </p>
            {prompt.hostedInvoiceUrl && (
              <HostedInvoiceLink
                label="Pay on Stripe's secure page instead"
                url={prompt.hostedInvoiceUrl}
              />
            )}
          </div>
        ))}
      {failure && (
        <FailureNotice
          failure={failure}
          onRefresh={refreshAmount}
        />
      )}
      <NextInvoiceLine date={prompt.nextInvoiceDate} />
      <PromptFooter>
        <PromptButton
          disabled={paying}
          onClick={onBack}
          variant='outline'
        >
          Back
        </PromptButton>
        <PromptButton
          disabled={!canPay}
          onClick={pay}
          variant='primary'
        >
          {paying && (
            <CircularProgress
              color='inherit'
              size={16}
            />
          )}
          {paying ? 'Processing...' : `Pay ${total}`}
        </PromptButton>
      </PromptFooter>
      {/* Not in the mock: a fallback to Stripe's hosted invoice page, kept below the footer. */}
      {prompt.hostedInvoiceUrl && !failure?.hostedInvoiceUrl && (
        <PromptExtras>
          <HostedInvoiceLink
            label="Or pay on Stripe's secure page"
            url={prompt.hostedInvoiceUrl}
          />
        </PromptExtras>
      )}
    </>
  )
}

export const PaymentDialog: FC<PaymentDialogProps> = ({ open, ...bodyProps }) => {
  // Bank accounts use Stripe.js too (its bank window), so the same Elements serve both.
  const key = publishableKey()
  const stripePromise = useMemo(() => (key ? loadStripe(key) : null), [key])
  // The focus trap stays on except while Stripe's bank window may be open.
  const [bankWindow, setBankWindow] = useState(false)
  return (
    <PromptDialog
      allowOutsideFocus={bankWindow}
      describedBy={DESCRIPTION_ID}
      labelledBy={TITLE_ID}
      open={open}
    >
      <Elements
        options={ELEMENTS_OPTIONS}
        stripe={stripePromise}
      >
        <PaymentDialogBody
          {...bodyProps}
          onBankWindow={setBankWindow}
        />
      </Elements>
    </PromptDialog>
  )
}
